import { inject, Injectable } from '@angular/core';
import { PaginatedResultState } from '../models/paginated-result-state';
import { NewsItem } from './models/news-item';
import { BehaviorSubject, map, Observable, of, Subject, switchMap } from 'rxjs';
import { NewsService } from './news.service';
import { NEWS_STATE_CONFIG } from './news-state.config';

@Injectable()
export class NewsStateService {
  private readonly initialState: PaginatedResultState<NewsItem> = {
    items: [],
    loading: false,
    hasNextPage: true, // initially assume there are more pages
    error: null
  };

  private stateSubject = new BehaviorSubject<PaginatedResultState<NewsItem>>(this.initialState);
  public state$: Observable<PaginatedResultState<NewsItem>> = this.stateSubject.asObservable();

  private lastCategoryId: string | null = null;
  private endCursor: string | null = null;

  private readonly config = inject(NEWS_STATE_CONFIG);
  private newsService = inject(NewsService);

  private loadNews$ = new Subject<{ categoryId: string | null; fetchPolicy?: string }>();

  constructor() {
    this.loadNews$.pipe(
      switchMap(({ categoryId, fetchPolicy }) =>
        this.newsService.getCompanyNews(this.config.pageSize, this.endCursor, categoryId, fetchPolicy)
      )
    ).subscribe({
      next: (connection) => {
        this.endCursor = connection.pageInfo.endCursor;
        this.setState({
          items: [...this.stateSubject.value.items, ...connection.nodes],
          loading: false,
          hasNextPage: connection.pageInfo.hasNextPage,
          error: null
        });
      },
      error: (error) => {
        this.setState({
          loading: false,
          error: error.message
        });
      }
    });

    this.loadNews();
  }

  public loadNews(categoryId: string | null = null, fetchPolicy?: string): void {
    this.setState({ loading: true, error: null });
    
    if (this.lastCategoryId != categoryId) {
      this.lastCategoryId = categoryId;
      this.clearNews();
    }

    this.loadNews$.next({ categoryId, fetchPolicy });
  }

  loadMore(): void {
    const currentState = this.stateSubject.value;
    if (!currentState.hasNextPage || currentState.loading)
      return;

    this.loadNews(this.lastCategoryId);
  }

  private setState(partialState: Partial<PaginatedResultState<NewsItem>>): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      ...partialState
    });
  }

  private clearNews(): void {
    this.endCursor = null;
    this.setState({
      items: [],
      hasNextPage: true
    });
  }

  getNewsItem(id: string): Observable<NewsItem> {
    const currentState = this.stateSubject.value;
    let item = currentState.items.find(n => n.id === id);
    if (item == null) {
      throw Error;
    }
    return of(item!);
  }

  refreshNews(): void {
    this.clearNews();
    this.loadNews(this.lastCategoryId, 'network-only');
  }

  // Getters for individual state slices
  get items$(): Observable<NewsItem[]> {
    return this.state$.pipe(map(state => state.items));
  }

  get loading$(): Observable<boolean> {
    return this.state$.pipe(map(state => state.loading));
  }

  get hasNextPage$(): Observable<boolean> {
    return this.state$.pipe(map(state => state.hasNextPage));
  }

  get error$(): Observable<string | null> {
    return this.state$.pipe(map(state => state.error));
  }
}
